/**
 * DosageLabel
 * ------------
 * Overlay badge that renders the dosage/volume ("84mg", "10ml") on
 * top of a product's bottle image. Sits in the top-left corner of
 * the image container by default; the parent controls layout via
 * standard absolute positioning.
 *
 * Contract:
 *   product        — must have `.name` (source of the value+unit
 *                    via `extractDosageLabel`) and optionally
 *                    `.dosage_label_size` (pixel font size, 0..200;
 *                    0 hides the badge; falsy → default 48px).
 *   accent         — override for the badge background colour. Falls
 *                    back to product.accent then a safe deep-navy.
 *
 * Non-negotiable UX bits:
 *   • The badge sits INSIDE a relatively-positioned parent so the
 *     absolute positioning stays scoped.
 *   • Reads dosage from the title (not the legacy `dosage` field)
 *     per the customer spec — "use the value found in the title
 *     for each product".
 *   • Bacteriostatic Water 10ml → "10ml"; new SKU "Bac Water 30ml"
 *     → "30ml" without any code change. Same regex covers both.
 *   • Returns null when the regex doesn't hit a supported unit, so
 *     bundles / accessories without a dose stay clean.
 */
import { extractDosageLabel, formatDosageLabel } from "../lib/dosageLabel";

export default function DosageLabel({ product, accent }) {
  const parts = extractDosageLabel(product?.name);
  if (!parts) return null;

  // Default 48px matches the badge sizing used on the Shop grid
  // designs; individual products can override via the admin panel's
  // "Dosage label size" input. A stored value of 0 opts out of the
  // badge entirely without deleting the config.
  const raw = product?.dosage_label_size;
  const size = raw === 0 ? 0 : Number(raw) || 48;
  if (size === 0) return null;

  const bg = accent || product?.accent || "#0f1e33";

  return (
    <span
      aria-hidden="true"
      data-testid={`dosage-label-${product.id}`}
      className="dosage-badge pointer-events-none absolute top-3 left-3 inline-flex items-center rounded-full border border-white/40 font-display font-[800] tracking-tight text-white shadow-lg"
      style={{
        // Padding scales with the font size so the badge always feels
        // like a proper pill regardless of the customer's chosen size.
        // `em` units + inline-flex is what keeps the pill balanced
        // whether it says "10ml" or "150mg".
        fontSize: `${size}px`,
        padding: `${size * 0.08}px ${size * 0.24}px`,
        lineHeight: 1,
        backgroundColor: bg,
      }}
    >
      {formatDosageLabel(parts)}
    </span>
  );
}
